/**
 * Helpers for rendering options-implied probability on the historical price chart.
 */

export interface PriceDistributionBucket {
  low: number | null;
  high: number | null;
  probability_pct: number;
  label: string;
  is_atm: boolean;
}

/** Key strikes and positioning context for the chart legend. */
export interface OptionsChartExpirationLevels {
  directionalBias: string | null;
  putCallOiRatio: number | null;
  putCallVolumeRatio: number | null;
  positioningNote: string | null;
  atmStrike: number | null;
  expectedMovePct: number | null;
  expectedMoveDollars: number | null;
  maxPainStrike: number | null;
  maxPainDistancePct: number | null;
  callWallStrike: number | null;
  putWallStrike: number | null;
  gexFlipStrike: number | null;
  gexCharacterization: string | null;
  /** Nearest OTM call strike above spot — P(close above). */
  nearestAboveStrike: number | null;
  nearestAboveProbPct: number | null;
  /** Nearest OTM put strike below spot — P(close below). */
  nearestBelowStrike: number | null;
  nearestBelowProbPct: number | null;
}

/** Vol, flow, delta, and narrative context for the chart legend. */
export interface OptionsChartInvestorContext {
  atmCallIvPct: number | null;
  atmPutIvPct: number | null;
  ivAssessment: string | null;
  otmCallIvPct: number | null;
  otmPutIvPct: number | null;
  ivSkewCharacterization: string | null;
  ivSkewNote: string | null;
  netDeltaOi: number | null;
  deltaOiBias: string | null;
  callOi: number | null;
  putOi: number | null;
  /** P(close above current spot) from delta ladder. */
  probAboveSpotPct: number | null;
  /** P(close below current spot) from delta ladder. */
  probBelowSpotPct: number | null;
  peakRangeLabel: string | null;
  peakRangeProbPct: number | null;
  insightLine: string | null;
  confidenceLevel: string | null;
  analysisTimestamp: string | null;
}

export interface OptionsChartOverlaySummary {
  spot: number;
  probAbovePct: number;
  probBelowPct: number;
  probAtmPct: number;
  upperTarget: number;
  lowerTarget: number;
  expiration: string;
  dte: number | null;
  levels: OptionsChartExpirationLevels;
  investor: OptionsChartInvestorContext;
}

/** Estimated size of the HTML distribution panel (for anchor clamping). */
export const OPTIONS_LEGEND_WIDTH_PX = 420;
export const OPTIONS_LEGEND_EST_HEIGHT_PX = 680;
const LEGEND_ANCHOR_GAP_PX = 10;

type ChartAxisMetrics = {
  xAxisMap?: Record<string, { scale?: (v: string) => number; bandwidth?: () => number; domain?: () => string[] }>;
  yAxisMap?: Record<string, { scale?: (v: number) => number }>;
  offset?: { left: number; top: number; width: number; height: number };
};

export function summarizePriceDistribution(
  buckets: PriceDistributionBucket[],
  spot: number,
): Pick<OptionsChartOverlaySummary, 'probAbovePct' | 'probBelowPct' | 'probAtmPct'> {
  let probAbovePct = 0;
  let probBelowPct = 0;
  let probAtmPct = 0;

  for (const b of buckets) {
    if (b.is_atm) {
      probAtmPct += b.probability_pct;
      continue;
    }
    const lo = b.low;
    const hi = b.high;
    const mid =
      lo != null && hi != null ? (lo + hi) / 2 : lo ?? hi ?? spot;
    if (mid >= spot) probAbovePct += b.probability_pct;
    else probBelowPct += b.probability_pct;
  }

  return {
    probAbovePct: round1(probAbovePct),
    probBelowPct: round1(probBelowPct),
    probAtmPct: round1(probAtmPct),
  };
}

/** Cumulative P(> spot) from call delta ladder (interpolate if needed). */
export function probAboveSpotFromTargets(
  targets: Array<{ price: number; probability_pct: number; direction: string }>,
  spot: number,
): number {
  const calls = targets
    .filter((t) => t.direction === 'above')
    .sort((a, b) => a.price - b.price);
  if (!calls.length) return 0;

  const atOrBelow = calls.filter((t) => t.price <= spot);
  const above = calls.filter((t) => t.price > spot);
  if (!above.length) {
    return round1(atOrBelow[atOrBelow.length - 1]?.probability_pct ?? 100);
  }
  if (!atOrBelow.length) {
    return round1(100);
  }

  const lo = atOrBelow[atOrBelow.length - 1];
  const hi = above[0];
  if (lo.price === hi.price) return round1(lo.probability_pct);
  const t = (spot - lo.price) / (hi.price - lo.price);
  const p = lo.probability_pct + t * (hi.probability_pct - lo.probability_pct);
  return round1(Math.max(0, Math.min(100, 100 - p + lo.probability_pct)));
}

/** Cumulative P(< spot) from put delta ladder. */
export function probBelowSpotFromTargets(
  targets: Array<{ price: number; probability_pct: number; direction: string }>,
  spot: number,
): number {
  const puts = targets
    .filter((t) => t.direction === 'below')
    .sort((a, b) => b.price - a.price);
  if (!puts.length) return 0;

  const atOrAbove = puts.filter((t) => t.price >= spot);
  const below = puts.filter((t) => t.price < spot);
  if (!below.length) {
    return round1(atOrAbove[atOrAbove.length - 1]?.probability_pct ?? 100);
  }
  if (!atOrAbove.length) {
    return round1(100);
  }

  const hi = atOrAbove[atOrAbove.length - 1];
  const lo = below[0];
  if (lo.price === hi.price) return round1(lo.probability_pct);
  const t = (spot - lo.price) / (hi.price - lo.price);
  const p = lo.probability_pct + t * (hi.probability_pct - lo.probability_pct);
  return round1(Math.max(0, Math.min(100, p)));
}

export type OptionsChartOverlayInput = {
  current_price: number;
  primary_expiration?: string;
  dte?: number;
  expected_move?: {
    upper_target?: number;
    lower_target?: number;
    estimated_move_pct?: number;
    estimated_move_dollars?: number;
    atm_strike?: number;
  };
  volume_and_open_interest?: {
    put_call_oi_ratio?: number;
    put_call_volume_ratio?: number;
    call_open_interest?: number;
    put_open_interest?: number;
    positioning_interpretation?: string;
  };
  implied_volatility?: {
    atm_call_iv?: number;
    atm_put_iv?: number;
    iv_assessment?: string;
  };
  iv_skew?: {
    otm_call_iv?: number;
    otm_put_iv?: number;
    skew_value?: number;
    characterization?: string;
  };
  delta_positioning?: {
    net_delta_oi?: number;
    directional_bias?: string;
  };
  max_pain?: { strike?: number; distance_pct?: number };
  gamma_exposure?: { gex_flip_strike?: number | null; characterization?: string };
  strike_walls?: {
    largest_call_wall?: { strike?: number } | null;
    largest_put_wall?: { strike?: number } | null;
  };
  price_distribution?: PriceDistributionBucket[];
  probability_targets?: Array<{
    price: number;
    probability_pct: number;
    direction: string;
  }>;
  directional_bias?: string | null;
  interpretation?: {
    ai_narrative?: string;
    summary?: string;
    confidence_level?: string;
  };
  analysis_timestamp?: string;
};

function targetAtStrike(
  targets: Array<{ price: number; probability_pct: number; direction: string }>,
  strike: number,
  direction: 'above' | 'below',
) {
  return targets.find((t) => t.direction === direction && t.price === strike);
}

function pickNearestLadderTargets(
  targets: Array<{ price: number; probability_pct: number; direction: string }>,
  spot: number,
  callWallStrike: number | null,
  putWallStrike: number | null,
): Pick<
  OptionsChartExpirationLevels,
  | 'nearestAboveStrike'
  | 'nearestAboveProbPct'
  | 'nearestBelowStrike'
  | 'nearestBelowProbPct'
> {
  const nearestAbove = targets
    .filter((t) => t.direction === 'above' && t.price > spot)
    .sort((a, b) => a.price - b.price)[0];
  const nearestBelow = targets
    .filter((t) => t.direction === 'below' && t.price < spot)
    .sort((a, b) => b.price - a.price)[0];

  const aboveStrike =
    callWallStrike != null && callWallStrike > spot ? callWallStrike : nearestAbove?.price;
  const belowStrike =
    putWallStrike != null && putWallStrike < spot ? putWallStrike : nearestBelow?.price;

  const above =
    (aboveStrike != null ? targetAtStrike(targets, aboveStrike, 'above') : null) ?? nearestAbove;
  const below =
    (belowStrike != null ? targetAtStrike(targets, belowStrike, 'below') : null) ?? nearestBelow;

  return {
    nearestAboveStrike: above?.price ?? null,
    nearestAboveProbPct: above != null ? round1(above.probability_pct) : null,
    nearestBelowStrike: below?.price ?? null,
    nearestBelowProbPct: below != null ? round1(below.probability_pct) : null,
  };
}

function buildExpirationLevels(
  options: OptionsChartOverlayInput,
  spot: number,
): OptionsChartExpirationLevels {
  const targets = options.probability_targets ?? [];
  const walls = options.strike_walls;
  const ladder = pickNearestLadderTargets(
    targets,
    spot,
    walls?.largest_call_wall?.strike ?? null,
    walls?.largest_put_wall?.strike ?? null,
  );
  const voi = options.volume_and_open_interest;
  const em = options.expected_move;
  const mp = options.max_pain;
  const gex = options.gamma_exposure;

  return {
    directionalBias: options.directional_bias ?? null,
    putCallOiRatio:
      voi?.put_call_oi_ratio != null ? Math.round(voi.put_call_oi_ratio * 100) / 100 : null,
    putCallVolumeRatio:
      voi?.put_call_volume_ratio != null
        ? Math.round(voi.put_call_volume_ratio * 100) / 100
        : null,
    positioningNote: voi?.positioning_interpretation ?? null,
    atmStrike: em?.atm_strike ?? null,
    expectedMovePct: em?.estimated_move_pct != null ? round1(em.estimated_move_pct) : null,
    expectedMoveDollars:
      em?.estimated_move_dollars != null ? round1(em.estimated_move_dollars) : null,
    maxPainStrike: mp?.strike ?? null,
    maxPainDistancePct: mp?.distance_pct != null ? round1(mp.distance_pct) : null,
    callWallStrike: walls?.largest_call_wall?.strike ?? null,
    putWallStrike: walls?.largest_put_wall?.strike ?? null,
    gexFlipStrike: gex?.gex_flip_strike ?? null,
    gexCharacterization: gex?.characterization ?? null,
    ...ladder,
  };
}

function ivPct(raw: number | undefined): number | null {
  if (raw == null || !Number.isFinite(raw)) return null;
  return round1(raw * 100);
}

function formatSkewNote(characterization: string | undefined, skewValue: number | undefined): string {
  const skewPct =
    skewValue != null && Number.isFinite(skewValue) ? `${(skewValue * 100).toFixed(1)}%` : null;
  switch (characterization) {
    case 'bearish_skew':
      return skewPct
        ? `Puts richer than calls by ${skewPct} — downside hedging priced in`
        : 'Puts richer than calls — downside hedging priced in';
    case 'bullish_skew':
      return skewPct
        ? `Calls richer than puts by ${skewPct} — upside demand priced in`
        : 'Calls richer than puts — upside demand priced in';
    default:
      return 'Balanced OTM call vs put implied vol';
  }
}

function findPeakDistributionBucket(
  buckets: PriceDistributionBucket[],
): { label: string; probabilityPct: number; isAtm: boolean } | null {
  if (!buckets.length) return null;
  const peak = buckets.reduce((best, b) =>
    b.probability_pct > best.probability_pct ? b : best,
  );
  return {
    label: formatDistributionLabel(peak),
    probabilityPct: round1(peak.probability_pct),
    isAtm: peak.is_atm,
  };
}

function buildInvestorContext(
  options: OptionsChartOverlayInput,
  spot: number,
  buckets: PriceDistributionBucket[],
): OptionsChartInvestorContext {
  const targets = options.probability_targets ?? [];
  const voi = options.volume_and_open_interest;
  const iv = options.implied_volatility;
  const skew = options.iv_skew;
  const delta = options.delta_positioning;
  const peak = findPeakDistributionBucket(buckets);
  const narrative =
    options.interpretation?.ai_narrative ?? options.interpretation?.summary ?? null;

  return {
    atmCallIvPct: ivPct(iv?.atm_call_iv),
    atmPutIvPct: ivPct(iv?.atm_put_iv),
    ivAssessment: iv?.iv_assessment ?? null,
    otmCallIvPct: ivPct(skew?.otm_call_iv),
    otmPutIvPct: ivPct(skew?.otm_put_iv),
    ivSkewCharacterization: skew?.characterization ?? null,
    ivSkewNote: formatSkewNote(skew?.characterization, skew?.skew_value),
    netDeltaOi:
      delta?.net_delta_oi != null ? Math.round(delta.net_delta_oi) : null,
    deltaOiBias: delta?.directional_bias ?? null,
    callOi: voi?.call_open_interest != null ? Math.round(voi.call_open_interest) : null,
    putOi: voi?.put_open_interest != null ? Math.round(voi.put_open_interest) : null,
    probAboveSpotPct: targets.length ? probAboveSpotFromTargets(targets, spot) : null,
    probBelowSpotPct: targets.length ? probBelowSpotFromTargets(targets, spot) : null,
    peakRangeLabel: peak?.label ?? null,
    peakRangeProbPct: peak?.probabilityPct ?? null,
    insightLine: narrative ?? null,
    confidenceLevel: options.interpretation?.confidence_level ?? null,
    analysisTimestamp: options.analysis_timestamp ?? null,
  };
}

/** True when the API payload has enough quantitative data to render the chart layer. */
export function hasOptionsPositioningChartData(
  oa: OptionsChartOverlayInput | null | undefined,
): oa is OptionsChartOverlayInput {
  if (!oa?.current_price || oa.current_price <= 0) return false;
  const hasDistribution = (oa.price_distribution?.length ?? 0) > 0;
  const hasTargets = (oa.probability_targets?.length ?? 0) > 0;
  return hasDistribution || hasTargets;
}

export function buildOptionsChartOverlaySummary(
  options: OptionsChartOverlayInput,
): OptionsChartOverlaySummary | null {
  const spot = options.current_price;
  if (!spot || spot <= 0) return null;

  const dist = options.price_distribution ?? [];
  const targets = options.probability_targets ?? [];

  let probAbovePct: number;
  let probBelowPct: number;
  let probAtmPct: number;

  if (dist.length > 0) {
    const s = summarizePriceDistribution(dist, spot);
    probAbovePct = s.probAbovePct;
    probBelowPct = s.probBelowPct;
    probAtmPct = s.probAtmPct;
  } else if (targets.length > 0) {
    probAbovePct = probAboveSpotFromTargets(targets, spot);
    probBelowPct = probBelowSpotFromTargets(targets, spot);
    probAtmPct = round1(Math.max(0, 100 - probAbovePct - probBelowPct));
  } else {
    return null;
  }

  const em = options.expected_move;
  const upperTarget = em?.upper_target ?? spot * 1.05;
  const lowerTarget = em?.lower_target ?? spot * 0.95;

  return {
    spot,
    probAbovePct,
    probBelowPct,
    probAtmPct,
    upperTarget,
    lowerTarget,
    expiration: options.primary_expiration ?? '',
    dte: options.dte ?? null,
    levels: buildExpirationLevels(options, spot),
    investor: buildInvestorContext(options, spot, dist),
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export interface DistributionLegendRow {
  label: string;
  probabilityPct: number;
  isAtm: boolean;
  isPeak?: boolean;
  isAggregate?: boolean;
}

export interface LadderLegendRow {
  label: string;
  probabilityPct: number;
  direction: 'above' | 'below';
}

function formatStrike(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** Human-readable bucket label (drops redundant decimals). */
export function formatDistributionLabel(bucket: PriceDistributionBucket): string {
  if (bucket.label.includes('∞')) {
    if (bucket.low == null && bucket.high != null) {
      return `Below $${formatStrike(bucket.high)}`;
    }
    if (bucket.high == null && bucket.low != null) {
      return `Above $${formatStrike(bucket.low)}`;
    }
  }
  if (bucket.is_atm && bucket.low != null && bucket.high != null) {
    return `$${formatStrike(bucket.low)} – $${formatStrike(bucket.high)}`;
  }
  return bucket.label
    .replace(/\u2212/g, '-')
    .replace(/(\d+)\.00/g, '$1');
}

function bucketMid(bucket: PriceDistributionBucket, spot: number): number {
  const lo = bucket.low;
  const hi = bucket.high;
  if (lo != null && hi != null) return (lo + hi) / 2;
  return lo ?? hi ?? spot;
}

/** Chart legend: highest price ranges at the top, lowest at the bottom. */
function orderLegendRowsHighToLow(rows: DistributionLegendRow[]): DistributionLegendRow[] {
  return [...rows].reverse();
}

/**
 * Investor-grade distribution rows: always show ATM + meaningful bands near spot;
 * aggregate only thin far tails.
 */
export function buildInvestorDistributionRows(
  buckets: PriceDistributionBucket[],
  spot: number,
  minBandPct = 2.5,
): DistributionLegendRow[] {
  if (!buckets.length) return [];

  const peak = buckets.reduce((best, b) =>
    b.probability_pct > best.probability_pct ? b : best,
  );

  const significant = buckets.filter(
    (b) => b.is_atm || b.probability_pct >= minBandPct,
  );
  const crumbs = buckets.filter((b) => !significant.includes(b));

  const rows: DistributionLegendRow[] = significant.map((b) => ({
    label: formatDistributionLabel(b),
    probabilityPct: round1(b.probability_pct),
    isAtm: b.is_atm,
    isPeak: b === peak,
    isAggregate: false,
  }));

  const belowCrumbs = crumbs.filter((b) => bucketMid(b, spot) < spot);
  const aboveCrumbs = crumbs.filter((b) => bucketMid(b, spot) >= spot);

  const belowOther = round1(belowCrumbs.reduce((s, b) => s + b.probability_pct, 0));
  if (belowOther >= 0.5) {
    rows.push({
      label: 'Other downside (thin tails)',
      probabilityPct: belowOther,
      isAtm: false,
      isAggregate: true,
    });
  }

  const aboveOther = round1(aboveCrumbs.reduce((s, b) => s + b.probability_pct, 0));
  if (aboveOther >= 0.5) {
    rows.push({
      label: 'Other upside (thin tails)',
      probabilityPct: aboveOther,
      isAtm: false,
      isAggregate: true,
    });
  }

  return orderLegendRowsHighToLow(rows);
}

/** Strikes nearest spot on the delta ladder (for expandable detail). */
export function buildNearSpotLadderRows(
  targets: Array<{ price: number; probability_pct: number; direction: string }>,
  spot: number,
  maxPerSide = 4,
): LadderLegendRow[] {
  const above = targets
    .filter((t) => t.direction === 'above')
    .sort((a, b) => a.price - b.price)
    .filter((t) => t.price >= spot - 2.5)
    .slice(0, maxPerSide);
  const below = targets
    .filter((t) => t.direction === 'below')
    .sort((a, b) => b.price - a.price)
    .filter((t) => t.price <= spot + 2.5)
    .slice(0, maxPerSide);

  return [
    ...above.map((t) => ({
      label: `> $${formatStrike(t.price)}`,
      probabilityPct: round1(t.probability_pct),
      direction: 'above' as const,
    })),
    ...below.map((t) => ({
      label: `< $${formatStrike(t.price)}`,
      probabilityPct: round1(t.probability_pct),
      direction: 'below' as const,
    })),
  ];
}

/**
 * @deprecated Prefer buildInvestorDistributionRows for chart legend.
 */
export function buildDistributionLegendRows(
  buckets: PriceDistributionBucket[],
  spot: number,
  maxRows = 7,
): DistributionLegendRow[] {
  const rows = buildInvestorDistributionRows(buckets, spot);
  if (rows.length <= maxRows) return rows;
  return rows.slice(0, maxRows);
}

/** Plot X of the last visible bar (where the price line ends). */
function lastBarCenterX(
  chartProps: ChartAxisMetrics,
  chartData: Array<{ date: string }>,
): number | null {
  const xAxisMap = chartProps.xAxisMap;
  const offset = chartProps.offset;
  if (!xAxisMap || !offset || chartData.length === 0) return null;

  const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
  const xScale = xAxis?.scale;
  if (!xScale) return null;

  const plotLeft = offset.left;
  const plotW = offset.width;
  const lastDate = chartData[chartData.length - 1]?.date;
  if (!lastDate) return null;

  const bw = typeof xAxis.bandwidth === 'function' ? xAxis.bandwidth() : 0;
  const x0 = xScale(lastDate);
  if (x0 == null || Number.isNaN(Number(x0))) {
    return plotLeft + plotW / 2;
  }
  return Number(x0) + (bw > 0 ? bw / 2 : 0);
}

/** Plot Y for a price level on the price axis. */
function priceToPlotY(chartProps: ChartAxisMetrics, price: number): number | null {
  const yAxisMap = chartProps.yAxisMap;
  const offset = chartProps.offset;
  if (!yAxisMap || !offset) return null;

  const yAxis = yAxisMap.price ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  const yScale = yAxis?.scale;
  if (typeof yScale !== 'function') return null;

  const y = Number(yScale(price));
  return Number.isFinite(y) ? y : null;
}

/**
 * Screen position for the distribution panel anchored at the price-line endpoint.
 * Panel sits just left of the last bar, vertically aligned with spot/options price.
 */
export function computeOptionsLegendScreenPosition(
  svg: SVGSVGElement,
  wrap: HTMLElement,
  chartProps: ChartAxisMetrics,
  chartData: Array<{ date: string }>,
  anchorPrice: number,
  legendWidth = OPTIONS_LEGEND_WIDTH_PX,
  legendHeight = OPTIONS_LEGEND_EST_HEIGHT_PX,
): { left: number; top: number } | null {
  const offset = chartProps.offset;
  if (!offset) return null;

  const anchorX = lastBarCenterX(chartProps, chartData);
  const anchorY = priceToPlotY(chartProps, anchorPrice);
  if (anchorX == null || anchorY == null) return null;

  const ctm = svg.getScreenCTM();
  if (!ctm) return null;

  const pt = svg.createSVGPoint();
  pt.x = anchorX;
  pt.y = anchorY;
  const scr = pt.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();

  let left = scr.x - wrapRect.left - legendWidth - LEGEND_ANCHOR_GAP_PX;
  let top = scr.y - wrapRect.top - legendHeight * 0.35;

  const plotTop = offset.top;
  const plotBottom = offset.top + offset.height;
  const ptMin = svg.createSVGPoint();
  ptMin.x = offset.left;
  ptMin.y = plotTop;
  const scrMin = ptMin.matrixTransform(ctm);
  const ptMax = svg.createSVGPoint();
  ptMax.x = offset.left + offset.width;
  ptMax.y = plotBottom;
  const scrMax = ptMax.matrixTransform(ctm);

  const minLeft = scrMin.x - wrapRect.left + 4;
  const maxLeft = scrMax.x - wrapRect.left - legendWidth - 4;
  const minTop = scrMin.y - wrapRect.top + 4;
  const maxTop = scrMax.y - wrapRect.top - legendHeight - 4;

  left = Math.max(minLeft, Math.min(left, maxLeft));
  top = Math.max(minTop, Math.min(top, maxTop));

  return { left, top };
}
